import { extendTheme } from "@chakra-ui/react";
import { ThemeConfig } from "@chakra-ui/theme";
import { Button } from "./button/button";
import { Table } from "./table/table";
import { Input } from "./input/input";

// Import fonts
import "@fontsource/poppins/300.css";
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/600.css";
import "@fontsource/fredoka-one/400.css";

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

export const theme = extendTheme({
  config,
  styles: {
    global: (props: any) => ({
      "html, body": {
        margin: 0,
        padding: 0,
        color: "gray.600",
        backgroundColor: "cherryUi.800",
        overflowX: "hidden",
      },
      "h1, h2, h3, h4, h5, h6": {
        color: props.colorMode === "light" ? "gray.600" : "white",
      },
    }),
  },
  colors: {
    black: "#000",
    white: "#fff",
    microsoftBlue: {
      500: "#005DA6",
      600: "#0078d4",
    },
    // Color scheme for cherryhub buttons
    aCherryGrey: {
      1100: "#46556880",
    },
    cherryGrey: {
      150: "#EEEEEE",
      1100: "#465568",
    },
    cherryRed: {
      100: "#BC1032",
    },
    cherryBackground: {
      100: "#7a111e",
      placeholder: "#f1f5f8",
      selectedTabItem: "#ffe0e5",
      tile: "#F8F9FB",
      subtleTile: "#F9FAFB",
      tag: "#F3F4F6",
    },
    cherryPink: {
      100: "#FFE2E7",
    },
    membershipRequestStatus: {
      incomplete: {
        bg: "#EEEEEE",
        text: "#465568",
      },
      awaitingReview: {
        bg: "#F59E0B",
        text: "#FFFFFF",
      },
      accepted: {
        bg: "#5ABB5E",
        text: "#FFFFFF",
      },
      rejected: {
        bg: "#EE3443",
        text: "#FFFFFF",
      },
      error: {
        bg: "#9E1029",
        text: "#FFFFFF",
      },
      createdInstantly: {
        bg: "#3B82F6",
        text: "#FFFFFF",
      },
    },
    cherryBorder: {
      100: "#dddcdd",
      200: "#C7C6C7",
      800: "#444145",
    },
    cherryBackgroundHover: {
      100: "#b07078",
    },
    cherryButton: {
      25: "#ffe6e3", // hover
      50: "#ED8C83",
      100: "#e04d4c",
      500: "#ee3443", // bg
      600: "#bb2834", // hoverBg
      700: "#6e171e", // activeBg
      800: "#C51836", // border
    },
    cancelButton: {
      border: "#C7C6C7",
    },
    cherryButtonDark: {
      50: "#ED8C83",
      100: "#e04d4c",
      500: "#9e1029", // bg
      600: "#6b0a1b", // hoverBg
      700: "#510815", // activeBg
    },
    cherryUi: {
      tabIconSelected: "#c22846",
      600: "#c41836",
      700: "#9e1029",
      800: "#7a121e",
    },
    riskRating: {
      safe: {
        bg: "#EFFFF3",
        border: "#5ABB5E",
        icon: "#209924",
      },
      reviewRequired: {
        bg: "#FFF9E0",
        border: "#EFC416",
        icon: "#DAB314",
      },
      highRisk: {
        bg: "#FFE0E5",
        border: "#DC0000",
        icon: "#DC0000",
      },
    },
    campaignMessage: {
      bg: "#EFF4FE",
      label: "#2454FB",
    },
    spamWarning: {
      soft: {
        bg: "#FFF9E0",
        border: "#EFC416",
        icon: "#DAB316",
      },
      strong: {
        bg: "#FFF4E5",
        border: "#E68A00",
        icon: "#FF7A00",
      },
      highRisk: {
        bg: "#FEE2E2",
        border: "#DC0000",
        icon: "#DC0002",
      },
    },
  },
  fonts: {
    heading: "Poppins, Helvetica, sans-serif",
    body: "Poppins, Helvetica, sans-serif",
  },
  components: {
    Button,
    Table,
    Input,
    Checkbox: {
      defaultProps: {
        colorScheme: "cherryButton",
      },
    },
    Radio: {
      defaultProps: {
        colorScheme: "cherryButton",
      },
    },
    Header: {
      baseStyle: {
        fontWeight: "600",
      },
      variants: {
        content: {
          color: "blue",
        },
      },
    },
    Modal: {
      baseStyle: {
        header: {
          backgroundColor: "cherryUi.600",
          color: "#fff",
          borderTopRightRadius: "var(--chakra-radii-md)",
          borderTopLeftRadius: "var(--chakra-radii-md)",
        },
        body: {
          paddingTop: "var(--chakra-space-6)",
        },
        closeButton: {
          color: "#fff",
        },
      },
    },
    Text: {
      variants: {
        "view-label": {
          fontSize: "sm",
          textTransform: "uppercase",
          color: "aCherryGrey.1100",
        },
        "view-value": {
          fontSize: "sm",
          fontWeight: "semibold",
          color: "cherryGrey.1100",
        },
        "small-label": {
          fontSize: "xs",
          color: "whiteAlpha.700",
          textTransform: "uppercase",
        },
      },
    },
    Heading: {
      variants: {
        "view-heading": {
          fontSize: "xl",
          color: "cherryUi.600",
          paddingBottom: 4,
        },
      },
    },
    Tabs: {
      variants: {
        edit: {
          tab: {
            _selected: {
              color: "cherryGrey.900",
              bgColor: "white",
              borderTopLeftRadius: "md",
              borderTopRightRadius: "md",
              boxShadow: "md",
            },
            // bgColor: "cherryGrey.160",
            color: "cherryGrey.1100",
            px: 12,
            textTransform: "uppercase",
            fontSize: "sm",
          },
          tablist: {
            px: 6,
            borderBottom: "none",
            bgColor: "cherryGrey.150",
            pt: 2,
          },
          tabpanels: {
            boxShadow: "md",
            borderRadius: "lg",
            px: 4,
            pt: 8,
            pb: 10,
            bgColor: "white",
            position: "relative",
            zIndex: 99,
          },
        },
      },
    },
  },
});
